import React from 'react'
import styled from 'styled-components'
import { color } from '../../../theme'
import { PhotoIcon as ImageOutlined } from '@heroicons/react/24/outline'
import { Column, Row } from '../../../components/Common/Flex'
import { scaleAnimation } from '../../../theme/mixins'
import { IPopulatedUserPhoto } from '../../../models/user_model'
import IPhotoMiniature from '../../../models/photo_miniature'

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  position: relative;
  align-items: center;
  flex-direction: column;
`

const ImagesContainer = styled.div`
  z-index: 0;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
`

const ImageBox = styled.div<{ $src: string }>`
  background-image: url(${({ $src }) => $src});
  background-size: cover;
  background-position: center;
  display: flex;
  flex: 1;
  width: 100%;
`

const Content = styled.div<{ $animation: boolean }>`
  width: calc(100% - 20px);
  height: calc(100% - 20px);
  max-height: 200px;
  overflow: hidden;
  border: none;
  transition: all 0.2s ease-out;
  box-shadow: 5px 5px 20px ${color.lightBlackOpacity};
  background-color: ${color.background};
  cursor: pointer;
  position: relative;
  text-align: left;
  height: 320px;
  flex: 1;
  overflow: hidden;
  border-radius: 4px;

  &:active {
    outline: none;
  }

  &:focus {
    outline: 1px solid ${color.content};
  }

  ${({ $animation }) => $animation && scaleAnimation}
`

const AlbumInnerContent = styled.div`
  z-index: 1;
  position: absolute;
  padding: 0px 20px;
  background-color: #0000001c;
  width: 100%;
  height: 100%;
`

const AlbumTitle = styled.h3`
  margin-top: 8px;
  margin-bottom: 4px;
  color: ${color.content};
`

const AlbumContent = styled.p`
  margin-top: 0;
  padding-top: 0;
  color: ${color.content};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  overflow: hidden;
  line-clamp: 2;
  -webkit-box-orient: vertical;
`

export interface AlbumProps {
  style?: React.CSSProperties
  albumColor?: string
  photosCount: number | string
  name: string
  description: string
  onClick?: VoidFunction
  coverPhotos?: Array<IPhotoMiniature>
}

const Album: React.FC<AlbumProps> = ({
  style,
  photosCount,
  albumColor,
  description,
  name,
  onClick,
  coverPhotos = [],
}) => {
  return (
    <Container style={style} onClick={onClick}>
      <Content
        style={{ backgroundColor: coverPhotos.length > 0 ? 'white' : albumColor }}
        $animation={Boolean(onClick)}
      >
        <ImagesContainer>
          {Boolean(coverPhotos) && coverPhotos.length > 0 && (
            <ImageBox $src={`${coverPhotos[0].urls.base}/small`} />
          )}
          {Boolean(coverPhotos) && coverPhotos.length > 1 && (
            <Column style={{ flex: 1, marginLeft: '2px' }}>
              <ImageBox $src={`${coverPhotos[1].urls.base}/small`} />
              {Boolean(coverPhotos) && coverPhotos.length > 2 && (
                <ImageBox style={{ marginTop: '2px' }} $src={`${coverPhotos[2].urls.base}/small`} />
              )}
            </Column>
          )}
        </ImagesContainer>
        <AlbumInnerContent>
          <Row style={{ color: color.content, position: 'absolute', bottom: 10, left: 20 }}>
            <ImageOutlined className="h-6 w-6 text-white" />
            <p style={{ margin: '1em 0.5em', color: 'white' }}>{photosCount}</p>
          </Row>
        </AlbumInnerContent>
      </Content>
      <Column style={{ width: 'calc(100% - 20px)' }}>
        <h4 className="flex mt-2 text-base font-bold">{name}</h4>
        <p className="flex text-xs">{description}</p>
      </Column>
    </Container>
  )
}

export default Album
